import { ElectronApi } from '@/services/electron.api';
import { getImageSize } from '@/utils/fs/image.utils';
import pngConvert, { blockByteDict } from '../../pngConvert';
import ezipImgConvert from '../../pngConvert/ezipImgConvert';
import lvglPyConvert from '../../pngConvert/lvglPyConvert';
import { fmtIndexEnum } from '../../pngConvert/fmtEnum';
import { uint16, uint32, uint8 } from '../../utils';
import { CompressMethod } from '../compiler';
import { DATA, FILE, IMG, IMG_ARRAY, SPRITE, TRANSLATION } from '../dataSource';
import {
  FILE_DATA_PRE_LENGTH,
  genId,
  genRecord,
  getRelateRecordUid
} from './utils';

export * from './genSlotData';
export * from './genWidgetData';
export * from './getConditionData';
export * from './genAnimationData';
export * from './utils';
const path = ElectronApi.path;
const fs = ElectronApi.fs;

const IMG_DATA_PRE_LENGTH = 12;
const IMG_ARRY_DATA_PRE_LENGTH = 12;
const SPRITE_SIMPLE_DATA_PRE_LENGTH = 12;
const DATA_ITEM_IMAGE_NUMBER_PRE_LENGTH = 20;
const DATA_ITEM_POINTER_DATA_PER_LENGTH = 32;
const DATA_ITEM_IMAGE_VALUES_PRE_LENGTH = 16;

const DATA_ITEM_ARC_PROGRESS_BAR_PRE_LENGTH = 40;
const DATA_ITEM_ARC_PROGRESS_BAR_INDICATOR_PRE_LENGTH = 52;
const DATA_ITEM_LINE_PROGRESS_BAR_PRE_LENGTH = 40;
const DATA_ITEM_LINE_PROGRESS_BAR_INDICATOR_PRE_LENGTH = 52;
const DATA_ITEM_TEXT = 40;
const VIDEO_DATA_PRE_LENGTH = 12;
const MULTI_SPRITE_PRE_LENGTH = 8;

const imgTypeListConvert: any = {
  truecolor_alpha: 'argb8888',
  truecolor: 'xrgb8888',
  alpha8: 'alpha8',
  indexed8: 'indexed8',
  rgb565: 'rgb565',
  argb4444: 'argb4444'
};

const imgType16bppListConvert: any = {
  truecolor_alpha: 'argb8565',
  truecolor: 'rgb565',
  alpha8: 'alpha8',
  indexed8: 'indexed8',
  rgb565: 'rgb565',
  argb4444: 'argb4444'
};

function transformEzipData(ezipImgBin: Buffer, curDataAddress: number): Buffer {
  // 宽高可以获取，不需要ezip的数据
  // const reserveAwAh = Buffer.from(ezipImgBin.subarray(1, 4));
  const originLenBin = Buffer.from(ezipImgBin?.subarray(4, 8));
  const ezipClipImgData = Buffer.from(ezipImgBin?.subarray(8));
  let align_size = 4 - (curDataAddress - Math.floor(curDataAddress / 4) * 4);
  if (align_size === 4) align_size = 0;
  let headerBin = Buffer.concat([originLenBin, uint32(align_size)]);
  for (let i = 0; i < align_size; i++) {
    headerBin = Buffer.concat([headerBin, uint8(0)]);
  }
  const transformRes = Buffer.concat([headerBin, ezipClipImgData]);
  return transformRes;
}
export async function genImageData(
  resource: Ast,
  xmlPath: string,
  curDataAddress: number,
  recordList?: any[],
  watchCompressMethod?: string,
  is16bpp = false,
  isN65Plus = false,
  isRotateImg = false,
  isN65C = false
): Promise<{ originData: any; originRecord: any }> {
  const {
    name,
    src: originSrc,
    compressMethod,
    format,
    recolorEnable,
    colorGroup
  } = resource.attrsMap;
  const imgFormat = is16bpp
    ? imgType16bppListConvert[format]
    : imgTypeListConvert[format];
  let fmtIndex: string = imgFormat ? fmtIndexEnum[imgFormat as any] : '0';
  const compress = (
    watchCompressMethod && watchCompressMethod !== ''
      ? watchCompressMethod
      : compressMethod === ''
      ? undefined
      : compressMethod
  ) as CompressMethod;
  const src = path?.resolve(xmlPath, `${originSrc}`);
  const size = await getImageSize(src);
  const width = size?.width || 0;
  const height = size?.height || 0;
  if (src) {
    let imgData: Buffer;

    if (isN65Plus) {
      const ezipImgBin = await ezipImgConvert(src, isRotateImg, isN65C);
      const ezipStartAddress = IMG_DATA_PRE_LENGTH + curDataAddress;
      if (ezipImgBin) {
        const imgFormat = Buffer.from(ezipImgBin.subarray(0, 1));
        fmtIndex = parseInt(imgFormat.toString('hex'), 16).toString();
        imgData = transformEzipData(ezipImgBin, ezipStartAddress);
      } else return { originData: {}, originRecord: {} };
    } else {
      //这里的imgData主要是根据format和src生成canvas版本的二进制，再根据compress默认为rle进行压缩返回二进制数据
      imgData = (await pngConvert(src, imgFormat, compress, is16bpp))!;
    }
    const type = IMG;
    const originData = {
      type,
      height,
      width,
      imgData,
      dataLength: imgData.length,
      totalLength: IMG_DATA_PRE_LENGTH + imgData.length,
      compressIndex: isN65Plus
        ? isRotateImg
          ? 0
          : 3
        : compress === 'RLE'
        ? 2
        : compress === 'RLEReversed'
        ? 1
        : 0,
      compressMethod: compress,
      recolorEnable,
      fmtIndex
    };
    let uid;
    if (colorGroup) {
      const colorSameNameRecord = recordList?.find((record) => {
        return record.name === name && record.type === IMG;
      });
      if (colorSameNameRecord) {
        const colorSameImgUid = colorSameNameRecord.uid.readInt32LE();
        const binaryUid = colorSameImgUid.toString(2).padStart(32, '0');
        const binaryConut = binaryUid.slice(13);
        const curColorImgCount = parseInt(binaryConut, 2);
        uid = genId(type, false, colorGroup, curColorImgCount);
      } else uid = genId(type, true, colorGroup);
    } else uid = genId(type);
    const originRecord = genRecord(
      type,
      name,
      uid,
      curDataAddress,
      originData.totalLength || 0,
      colorGroup
    );
    return { originData, originRecord };
  } else return { originData: {}, originRecord: {} };
}

export async function genImageArrayData(
  resource: Ast,
  xmlPath: string,
  curDataAddress: number,
  recordList?: any[],
  watchCompressMethod?: string,
  is16bpp = false,
  isN65Plus = false,
  isRotateImg = false,
  isN65C = false
): Promise<{ originData: any; originRecord: any }> {
  let width: number | undefined = 0;
  let height: number | undefined = 0;
  const type = IMG_ARRAY;
  const { name, compressMethod, format, recolorEnable, colorGroup } =
    resource.attrsMap;
  const imgFormat = is16bpp
    ? imgType16bppListConvert[format]
    : imgTypeListConvert[format];
  let fmtIndex: string = imgFormat ? fmtIndexEnum[imgFormat as any] : '0';
  const data = [];
  const compress = (
    watchCompressMethod &&
    watchCompressMethod !== 'None' &&
    watchCompressMethod !== ''
      ? watchCompressMethod
      : compressMethod === '' || compressMethod === 'None'
      ? undefined
      : compressMethod
  ) as CompressMethod;
  const imgNumber = resource.children.length;
  let ezipStartAddress =
    curDataAddress + IMG_ARRY_DATA_PRE_LENGTH + imgNumber * 4;
  for (let i = 0; i < resource.children.length; i++) {
    const { src: originSrc } = resource.children[i].attrsMap;
    const src = path?.resolve(xmlPath, `${originSrc}`);

    // 获取第一张图片宽高
    if (i === 0) {
      const size = await getImageSize(src);
      width = size?.width || 0;
      height = size?.height || 0;
    }

    if (src) {
      let dataItem: Buffer;

      if (isN65Plus) {
        const ezipImgBin = await ezipImgConvert(src, isRotateImg, isN65C);
        if (ezipImgBin) {
          const imgFormat = Buffer.from(ezipImgBin.subarray(0, 1));
          fmtIndex = parseInt(imgFormat.toString('hex'), 16).toString();
          dataItem = transformEzipData(ezipImgBin, ezipStartAddress);
          ezipStartAddress += dataItem.length;
        } else return { originData: {}, originRecord: {} };
      } else {
        dataItem = (await pngConvert(src, imgFormat, compress, is16bpp))!;
      }
      if (isN65Plus && dataItem) {
        const binPath = originSrc.replace('.png', '.bin');
        const resPath = path?.join(xmlPath, binPath);
        resPath && (await fs?.promises.writeFile(resPath, dataItem));
      }
      data.push(dataItem);
    }
  }
  const imgLengthInfo = data.map((i) => i.length); //含单个图片长度的数组

  const imgData = Buffer.concat(data);

  const dataLength = imgData.length;
  const imgArrayRecordLength =
    compress || isN65Plus
      ? IMG_ARRY_DATA_PRE_LENGTH + imgNumber * 4 + dataLength
      : IMG_ARRY_DATA_PRE_LENGTH + dataLength;
  const originData = {
    type,
    imgNumber,
    height,
    width,
    imgData,
    dataLength: dataLength,
    totalLength: imgArrayRecordLength,
    imgLengthInfo,
    isN65Plus,
    recolorEnable,
    compressIndex: isN65Plus
      ? isRotateImg
        ? 0
        : 3
      : compress === 'RLE'
      ? 2
      : compress === 'RLEReversed'
      ? 1
      : 0,
    compressMethod: compress,
    fmtIndex,
    dataAddressOffset: curDataAddress
  };

  let uid;
  if (colorGroup) {
    const colorSameNameRecord = recordList?.find((record) => {
      return record.name === name && record.type === IMG_ARRAY;
    });
    if (colorSameNameRecord) {
      const colorSameImgUid = colorSameNameRecord.uid.readInt32LE();
      const binaryUid = colorSameImgUid.toString(2).padStart(32, '0');
      const binaryConut = binaryUid.slice(13);
      const curColorImgCount = parseInt(binaryConut, 2);
      uid = genId(type, false, colorGroup, curColorImgCount);
    } else uid = genId(type, true, colorGroup);
  } else uid = genId(type);

  const originRecord = genRecord(
    type,
    name,
    uid,
    curDataAddress,
    originData.totalLength || 0,
    colorGroup
  );
  return { originData, originRecord };
}

export function genSpriteData(
  resource: Ast,
  curDataAddress: number,
  recordList: any[],
  originDataList: any[]
): { originData: any; originRecord: any } {
  const type = SPRITE;
  const { interval, repeatCount, ref, name } = resource.attrsMap;
  let relateRecordUid;
  let originData;
  if (ref.endsWith('mp4')) {
    relateRecordUid = getRelateRecordUid(ref, recordList, FILE);
    if (relateRecordUid) {
      originData = {
        type,
        uidVideoFile: relateRecordUid,
        totalLength: VIDEO_DATA_PRE_LENGTH,
        isVideo: true,
        repeatCount
      };
    } else return { originData: {}, originRecord: {} };
  } else {
    relateRecordUid = getRelateRecordUid(ref, recordList, IMG_ARRAY);
    if (relateRecordUid) {
      const relateRecord = recordList.find((record) => {
        return record.name === ref.slice(1) && record.type === IMG_ARRAY;
      });
      const relateData = originDataList.find(
        (data) => data.dataAddressOffset === relateRecord.dataAddress
      );
      const { imgNumber } = relateData;

      originData = {
        type,
        uidImgArray: relateRecordUid,
        interval,
        repeatCount,
        totalLength: SPRITE_SIMPLE_DATA_PRE_LENGTH,
        imgNum: imgNumber,
        isVideo: false
      };
    } else {
      relateRecordUid = getRelateRecordUid(ref, recordList, IMG);
      if (relateRecordUid) {
        originData = {
          type,
          uidImgArray: relateRecordUid,
          interval: 0,
          repeatCount: 0,
          totalLength: SPRITE_SIMPLE_DATA_PRE_LENGTH,
          imgNum: 1,
          isVideo: false
        };
      } else return { originData: {}, originRecord: {} };
    }
  }

  const uid = genId(type);
  const originRecord = genRecord(
    type,
    name,
    uid,
    curDataAddress,
    originData?.totalLength || 0
  );
  return { originData, originRecord };
}

export function genMultiSpriteData(
  resource: Ast,
  curDataAddress: number,
  recordList: any[]
) {
  const type = SPRITE;
  const { name, interval = '0', repeatCount, count } = resource.attrsMap;
  const spriteInfoData: any[] = [];
  resource.children.map((item) => {
    const { ref, default_index = 0, refExt } = item.attrsMap;
    const childType = refExt ? IMG : ref.endsWith('mp4') ? FILE : IMG_ARRAY;
    const imgArrayRecordUid = getRelateRecordUid(ref, recordList, childType);
    if (imgArrayRecordUid) {
      spriteInfoData.push({
        uid_img_array: imgArrayRecordUid,
        default_index
      });
    }
  });
  const originData = {
    type,
    count: Number(count),
    interval: Number(interval),
    repeatCount: Number(repeatCount),
    totalLength: MULTI_SPRITE_PRE_LENGTH + 8 * Number(count),
    spriteInfoData,
    isMultiSprite: true
  };
  const uid = genId(type);
  const originRecord = genRecord(
    type,
    name,
    uid,
    curDataAddress,
    originData?.totalLength || 0
  );
  return { originData, originRecord };
}

export function genDataItemImageNumberData(
  resource: Ast,
  curDataAddress: number,
  recordList: any[]
): { originData: any; originRecord: any } {
  const type = DATA;
  const {
    name,
    ref,
    totalDigits,
    decimalDigits,
    align,
    leadingZero,
    trailingZero,
    unitIcon,
    rotation,
    space,
    source,
    renderRule,
    decimalOffsetX,
    parameter
  } = resource.attrsMap;
  const relateRecordUid = getRelateRecordUid(ref, recordList, IMG_ARRAY);
  if (relateRecordUid) {
    const unitIconRecordUid =
      getRelateRecordUid(unitIcon, recordList, IMG) ?? 0;

    const originData = {
      type,
      uidImgArray: relateRecordUid,
      totalDigits,
      decimalDigits,
      align,
      leadingZero,
      trailingZero,
      unitIcon: unitIconRecordUid,
      rotation,
      parameter,
      totalLength: DATA_ITEM_IMAGE_NUMBER_PRE_LENGTH,
      space,
      tag: 'DataItemImageNumber',
      source,
      renderRule,
      decimalOffsetX,
      ref
    };
    const uid = genId(type);
    const originRecord = genRecord(
      type,
      name,
      uid,
      curDataAddress,
      originData.totalLength || 0
    );
    return { originData, originRecord };
  } else return { originData: {}, originRecord: {} };
}

export function genDataItemImageValuesData(
  resource: Ast,
  curDataAddress: number,
  recordList: any[]
): { originData: any; originRecord: any } {
  const type = DATA;
  const { name, ref, rotation, hasInvalidValueImage, source, parameter } =
    resource.attrsMap;
  const relateRecordUid = getRelateRecordUid(ref, recordList, IMG_ARRAY);
  if (relateRecordUid) {
    const posTable: number[][] = [];
    const valueTable: number[] = [];
    const { children } = resource;

    if (children && children.length > 0) {
      children.forEach((item: any) => {
        const { value, offsetX, offsetY } = item.attrsMap;
        if (value) {
          valueTable.push(value);
        }
        if (offsetX || offsetY) {
          posTable.push([offsetX || 0, offsetY || 0]);
        }
      });
    }
    let imgValuesFlags = 0;
    // flags一共两个bit：第0位表示是否有posTable，第一位表示是否有valueTable
    if (posTable.length > 0) {
      imgValuesFlags |= 0x01;
    }
    if (valueTable.length > 0) {
      imgValuesFlags |= 0x02;
    }
    if (hasInvalidValueImage) {
      imgValuesFlags |= 0x04;
    }
    const dataTable = [];
    // data table 根据flags取 01,10,11 三选一
    if (imgValuesFlags === 0x01) {
      dataTable.push(
        ...posTable.map((item: number[]) =>
          Buffer.concat([uint16(item[0]), uint16(item[1])])
        )
      );
    } else if (imgValuesFlags === 0x02 || imgValuesFlags === 0x06) {
      dataTable.push(
        ...valueTable.map((item: any) => {
          if (item === 'INT_MAX') return uint32(Math.pow(2, 31) - 1);
          else return uint32(item);
        })
      );
    } else if (imgValuesFlags === 0x03) {
      for (let i = 0; i < posTable.length; i++) {
        dataTable.push(
          uint16(posTable[i][0]),
          uint16(posTable[i][1]),
          uint32(valueTable[i])
        );
      }
    }
    const dataTableBinary = Buffer.concat(dataTable);

    const originData = {
      type,
      uidImgArray: relateRecordUid,
      dataTable: dataTableBinary,
      rotation,
      totalLength: DATA_ITEM_IMAGE_VALUES_PRE_LENGTH + dataTableBinary.length,
      source,
      tag: 'DataItemImageValues',
      flags: imgValuesFlags,
      parameter
    };
    const uid = genId(type);
    const originRecord = genRecord(
      type,
      name,
      uid,
      curDataAddress,
      originData.totalLength || 0
    );
    return { originData, originRecord };
  } else return { originData: {}, originRecord: {} };
}

export function genDataItemPointerData(
  resource: Ast,
  curDataAddress: number,
  recordList: any[]
): { originData: any; originRecord: any } {
  const type = DATA;
  const {
    name,
    ref,
    pivotX,
    pivotY,
    angleStart,
    valueStart,
    angleRange,
    valueRange,
    parameter,
    source,
    renderRule
  } = resource.attrsMap;
  const relateRecordUid = getRelateRecordUid(ref, recordList, IMG);
  if (relateRecordUid) {
    const originData = {
      type,
      pointerRefImg: relateRecordUid,
      pivotX,
      pivotY,
      angleStart,
      valueStart,
      angleRange,
      valueRange,
      parameter,
      totalLength: DATA_ITEM_POINTER_DATA_PER_LENGTH,
      tag: 'DataItemPointer',
      renderRule,
      source,
      ref
    };
    const uid = genId(type);
    const originRecord = genRecord(
      type,
      name,
      uid,
      curDataAddress,
      originData.totalLength || 0
    );
    return { originData, originRecord };
  } else return { originData: {}, originRecord: {} };
}

export function genDataItemArcProgressBarData(
  resource: Ast,
  curDataAddress: number,
  recordList: any[]
): { originData: any; originRecord: any } {
  const type = DATA;
  const {
    name,
    ref,
    bg: arcBgRef,
    pivotX: arcPivotX,
    pivotY: arcPivotY,
    angleStart,
    valueStart,
    angleRange,
    valueRange,
    barRadius,
    barWidth,
    endingStyle,
    indicatorImage,
    indicatorRadius,
    renderRule: renderRuleArc,
    source,
    parameter
  } = resource.attrsMap;
  const relateRecordUid = getRelateRecordUid(ref, recordList, IMG);
  if (relateRecordUid) {
    const bgRelateUid = getRelateRecordUid(arcBgRef, recordList, IMG) ?? 0;
    const indicatorRelateUid =
      getRelateRecordUid(indicatorImage, recordList, IMG) ?? 0;

    const originData = {
      type,
      tag: 'DataItemArcProgressBar',
      uidFgImg: relateRecordUid,
      uidBgImg: bgRelateUid,
      pivotX: arcPivotX,
      pivotY: arcPivotY,
      angleStart,
      valueStart,
      angleRange,
      valueRange,
      barRadius,
      barWidth,
      endingStyle,
      indicatorImage,
      uidIndicatorImg: indicatorRelateUid,
      indicatorRadius,
      source,
      renderRuleArc,
      parameter,
      totalLength: indicatorImage
        ? DATA_ITEM_ARC_PROGRESS_BAR_INDICATOR_PRE_LENGTH
        : DATA_ITEM_ARC_PROGRESS_BAR_PRE_LENGTH
    };
    const uid = genId(type);
    const originRecord = genRecord(
      type,
      name,
      uid,
      curDataAddress,
      originData.totalLength || 0
    );
    return { originData, originRecord };
  } else return { originData: {}, originRecord: {} };
}

export function genDataItemLineProgressBarData(
  resource: Ast,
  curDataAddress: number,
  recordList: any[]
): { originData: any; originRecord: any } {
  const type = DATA;
  const {
    name,
    ref,
    bg: lineBgRef,
    barWidth,
    startX,
    startY,
    endX,
    endY,
    valueStart,
    valueRange,
    endingStyle,
    indicatorImage,
    indicatorOffsetX,
    indicatorOffsetY,
    renderRule: renderRuleLine,
    source,
    parameter
  } = resource.attrsMap;
  const relateRecordUid = getRelateRecordUid(ref, recordList, IMG);
  if (relateRecordUid) {
    const bgRelateUid = getRelateRecordUid(lineBgRef, recordList, IMG) ?? 0;
    const indicatorRelateUid =
      getRelateRecordUid(indicatorImage, recordList, IMG) ?? 0;

    const originData = {
      type,
      tag: 'DataItemLineProgressBar',
      uidFgImg: relateRecordUid,
      uidBgImg: bgRelateUid,
      barWidth,
      startX,
      startY,
      endX,
      endY,
      valueStart,
      valueRange,
      endingStyle,
      indicatorImage,
      indicatorRelateUid,
      indicatorOffsetX,
      indicatorOffsetY,
      source,
      renderRuleLine,
      parameter,
      totalLength: indicatorImage
        ? DATA_ITEM_LINE_PROGRESS_BAR_INDICATOR_PRE_LENGTH
        : DATA_ITEM_LINE_PROGRESS_BAR_PRE_LENGTH
    };
    const uid = genId(type);
    const originRecord = genRecord(
      type,
      name,
      uid,
      curDataAddress,
      originData.totalLength || 0
    );
    return { originData, originRecord };
  } else return { originData: {}, originRecord: {} };
}

export function genDataItemTextData(
  text: Ast,
  curDataAddress: number,
  recordList: any[]
): { originData: any; originRecord: any } {
  const type = DATA;
  const { string, name, literal_crop_font, literal_num, longMode, parameter } =
    text.attrsMap;
  const textContents: string[] = [];
  text.children.map((textChild) => {
    textContents.push(textChild.attrsMap.source);
  });
  let _string = string;

  let translationUid: Buffer | 0 = 0;
  if (_string.indexOf('@') === 0) {
    translationUid = getRelateRecordUid(_string, recordList, TRANSLATION) ?? 0;
  }
  let literalUid: Buffer | 0 = 0;
  if (literal_crop_font || literal_num) {
    literalUid = getRelateRecordUid(literal_crop_font, recordList, FILE) ?? 0;
    _string = _string.replace(/\\(0|n|\')/g, (match, p1) => {
      const escapeMap: Record<string, string> = {
        '0': '\0',
        n: '\n',
        "'": "'"
      };
      return escapeMap[p1];
    });
    text.attrsMap.string = _string;
  }
  const totalLength =
    translationUid === 0
      ? DATA_ITEM_TEXT +
        2 * textContents.length +
        Buffer.from(_string).length +
        8
      : DATA_ITEM_TEXT + 2 * textContents.length + translationUid.length + 8;
  const originData = {
    type,
    contents: textContents,
    ...text.attrsMap,
    tag: 'DataItemText',
    translationUid,
    literalUid,
    longMode,
    totalLength,
    parameter
  };
  const uid = genId(type);
  const originRecord = genRecord(
    type,
    name,
    uid,
    curDataAddress,
    originData.totalLength || 0
  );
  return { originData, originRecord };
}

export function genTranslationData(
  translation: Ast,
  curDataAddress: number
): { originData: any; originRecord: any } {
  const typeofList = [
    'en_US',
    'zh_CN',
    'zh_TW',
    'ja_JP',
    'es_ES',
    'fr_FR',
    'de_DE',
    'ru_RU',
    'pt_BR',
    'pt_PT',
    'it_IT',
    'ko_KR',
    'tr_TR',
    'nl_NL',
    'th_TH',
    'sv_SE',
    'da_DK',
    'vi_VN',
    'nb_NO',
    'pl_PL',
    'fi_FI',
    'in_ID',
    'el_GR',
    'ro_RO',
    'cs_CZ',
    'uk_UA',
    'hu_HU',
    'sk_SK',
    'zh_HK',
    'iw_IL',
    'ar_EG',
    'lt_LT',
    'bg_BG'
  ];
  const { name } = translation.attrsMap;
  const type = TRANSLATION;
  let flagNumber = 0;
  let reserved = 0;
  let allStrBuffer = Buffer.alloc(0);
  const strBufferArray: { buffer: Buffer; index: number }[] = [];
  let strLengthBuffer = Buffer.alloc(0);
  translation.children.forEach((element: any) => {
    let _string = element.attrsMap.str.replace(/\\(0|n|\')/g, (match: string, p1: string) => {
      const escapeMap: Record<string, string> = {
        '0': '\0',
        n: '\n',
        "'": "'"
      };
      return escapeMap[p1];
    });
    // 希伯来语移动心电翻译要求必须有双引号，由于mac和win对从右到左镜像处理不同需要特殊处理
    if (element.attrsMap.language === 'iw_IL' && _string.includes('"'))
      _string = 'מכשיר לפרק כף היד למדידת אק"ג ולחץ דם';
    const strBuffer = Buffer.from(_string);
    const lIndex: number = typeofList.findIndex(
      (v) => v === element.attrsMap.language
    );
    strBufferArray.push({
      buffer: strBuffer,
      index: lIndex
    });
    if (lIndex < 0) {
      console.error('cannot find language index');
    }
    // >>> 0 强制将结果转为无符号32位整数
    if (lIndex <= 31) flagNumber = flagNumber + ((1 << lIndex) >>> 0);
    // 多语种增加，32位不够，多的依次放到reserved位上
    else reserved = reserved + (1 << (lIndex - 32));
  });
  strBufferArray.sort((a, b) => {
    return a.index - b.index;
  });
  strBufferArray.forEach((element: any) => {
    strLengthBuffer = Buffer.concat([
      strLengthBuffer,
      uint32(element.buffer.length)
    ]);
    allStrBuffer = Buffer.concat([allStrBuffer, element.buffer]);
  });
  const bufferData = Buffer.concat([
    uint32(flagNumber),
    uint32(reserved),
    strLengthBuffer,
    allStrBuffer
  ]);
  const originData = {
    type,
    translationData: bufferData,
    dataLength: bufferData.length,
    totalLength: bufferData.length
  };
  const uid = genId(type);
  const originRecord = genRecord(
    type,
    name,
    uid,
    curDataAddress,
    originData.totalLength || 0
  );
  return { originData, originRecord };
}

export async function genJSData(
  resource: Ast,
  jsPath: string,
  curDataAddress: number,
  watchCompressMethod?: string
): Promise<{ originData: any; originRecord: any }> {
  const fs = ElectronApi.fs;
  if (!path || !fs) return { originData: {}, originRecord: {} };
  const { name, fileName } = resource.attrsMap;
  const type = FILE;
  const src = path.resolve(jsPath, `${fileName}`);
  let jsBuffer = Buffer.alloc(0);
  if (fs.existsSync(src)) {
    jsBuffer = fs.readFileSync(src);
  } else {
    return genJSImageData(
      resource,
      jsPath,
      curDataAddress,
      watchCompressMethod
    );
  }
  const jsUid = genId(type);
  const originData = {
    type: FILE,
    name,
    fileName,
    dataLength: jsBuffer.length,
    totalLength: FILE_DATA_PRE_LENGTH + fileName.length + jsBuffer.length,
    jsBuffer
  };
  const originRecord = genRecord(
    type,
    name,
    jsUid,
    curDataAddress,
    originData.totalLength || 0
  );
  return { originData, originRecord };
}

export async function genJSImageData(
  resource: Ast,
  xmlPath: string,
  curDataAddress: number,
  watchCompressMethod?: string
): Promise<{ originData: any; originRecord: any }> {
  const { name, fileName } = resource.attrsMap;
  const type = FILE;
  const isRle =
    watchCompressMethod === 'RLE' || watchCompressMethod === 'RLEReversed';
  const compress = (
    watchCompressMethod &&
    watchCompressMethod !== 'None' &&
    watchCompressMethod !== ''
      ? watchCompressMethod
      : undefined
  ) as CompressMethod;
  const isRiveImge = fileName.startsWith('_rive');
  const src = path?.resolve(xmlPath, `${fileName.replace(/\.bin$/, '.png')}`);
  const riveName = fileName.replace(/\.png$/, '.bin');
  let fmt = fileName.split('/')[1];
  if (!(fmt in blockByteDict)) fmt = 'indexed8';
  if (src && fs) {
    const jsBuffer = isRiveImge
      ? await lvglPyConvert(src, fmt, isRle)
      : await pngConvert(src, fmt as string, compress, false);
    if (!jsBuffer) {
      return { originData: {}, originRecord: {} };
    }
    const uid = genId(type);
    const originData = {
      type,
      jsBuffer,
      dataLength: jsBuffer.length,
      totalLength: FILE_DATA_PRE_LENGTH + name.length + jsBuffer.length,
      name: isRiveImge ? riveName : name,
      fileName: isRiveImge ? riveName : fileName
    };
    const originRecord = genRecord(
      type,
      name,
      uid,
      curDataAddress,
      originData.totalLength || 0
    );
    return { originData, originRecord };
  } else return { originData: {}, originRecord: {} };
}

export const genData = async (
  resource: Ast,
  xmlPath: string,
  curDataAddress: number,
  recordList?: any[],
  watchCompressMethod?: string,
  is16bpp = false,
  isN65AB = false,
  isRotateImg = false
) => {
  const { tag } = resource;
  switch (tag) {
    case 'Image':
      return genImageData(
        resource,
        xmlPath,
        curDataAddress,
        recordList,
        watchCompressMethod,
        is16bpp,
        isN65AB,
        isRotateImg
      );
  }
};
